import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { refreshFeatureFlags } from "@/lib/useFeature";
import { clearRecentVisits } from "@/lib/recentVisits";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = no user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // No early return on missing localStorage token — the httpOnly
    // `scwt_auth` cookie may still authenticate (field magic-link
    // sessions survive mobile localStorage purges this way).
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("scwt_token");
      setUser(null);
    } finally {
      setLoading(false);
      refreshFeatureFlags();
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("scwt_token", data.token);
    setUser(data.user);
    await refreshFeatureFlags();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("scwt_token", data.token);
    setUser(data.user);
    await refreshFeatureFlags();
    return data.user;
  };

  // Phase 2J-AK — finalise a team-member invitation by setting their
  // password. Mirrors the login() shape so the consumer gets the user
  // back to redirect on.
  const acceptInvite = async (inviteToken, password) => {
    const { data } = await api.post(`/auth/invitations/${inviteToken}/accept`, { password });
    localStorage.setItem("scwt_token", data.token);
    setUser(data.user);
    await refreshFeatureFlags();
    return data.user;
  };

  const logout = async () => {
    localStorage.removeItem("scwt_token");
    clearRecentVisits();
    setUser(null);
    refreshFeatureFlags();
    // Feb 2026 — Auth Token Hardening (Phase A). Also tell the server
    // to clear the httpOnly `scwt_auth` cookie set on login.
    // Jun 2026 — MUST be awaited: callers do a full-page redirect right
    // after logout(), and an un-awaited POST gets aborted by the page
    // unload, leaving the cookie alive — /auth/me's cookie fallback then
    // silently restored the session on the next refresh.
    try {
      await api.post("/auth/logout");
    } catch {
      // network error must never block client-side sign out
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, acceptInvite, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
};
